import type { Label } from '@seen/core/models/label'
import type { NoteLabel } from '@seen/core/models/noteLabel'
import type { OrderLabel } from '@seen/core/models/orderLabel'
import type { TaskLabel } from '@seen/core/models/taskLabel/taskLabel'
import { extractId } from '@seen/core/utils'
import { useQueries } from '@tanstack/react-query'
import { compact } from 'lodash-es'
import type { JSX, ReactNode } from 'react'
import { DeletedLabelIndicator } from '#src/components/DeletedLabelIndicator.tsx'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'

export type LabelClass = typeof TaskLabel | typeof OrderLabel | typeof NoteLabel

interface LabelChangeActionProps {
  added: string[]
  removed: string[]
  LabelClass: LabelClass
}

export function LabelChangeAction({
  added,
  removed,
  LabelClass,
}: LabelChangeActionProps): JSX.Element {
  const addedLabels = useLabels(added, LabelClass)
  const removedLabels = useLabels(removed, LabelClass)

  return (
    <>
      {addedLabels.length > 0 && (
        <>
          added {addedLabels.length === 1 ? 'label' : 'labels'}{' '}
          <LabelList labels={addedLabels} />
        </>
      )}
      {addedLabels.length > 0 && removedLabels.length > 0 && ' and '}
      {removedLabels.length > 0 && (
        <>
          removed {removedLabels.length === 1 ? 'label' : 'labels'}{' '}
          <LabelList labels={removedLabels} />
        </>
      )}
    </>
  )
}

/*
 * Hook to fetch multiple labels in parallel and filter out any that fail to load.
 */
function useLabels(labelRefs: string[], LabelClass: LabelClass): Label[] {
  const medplum = useMedplum()

  const results = useQueries({
    queries: labelRefs.map((ref) => {
      const resolvedId = extractId(ref) ?? ''
      return {
        // oxlint-disable-next-line @tanstack/query/exhaustive-deps -- LabelClass is stable
        queryKey: cacheKeys.readExtended(
          LabelClass.RESOURCE_TYPE,
          resolvedId,
          LabelClass.name
        ),
        queryFn: async () => medplum.readResourceClass(LabelClass, resolvedId),
        enabled: Boolean(resolvedId),
      }
    }),
  })

  return compact(results.map((r) => r.data))
}

function LabelList({ labels }: { labels: Label[] }): ReactNode {
  return (
    <>
      {labels.map((label, i) => (
        <span key={label.id}>
          {i > 0 && (i === labels.length - 1 ? ' and ' : ', ')}
          <LabelItem label={label} />
        </span>
      ))}
    </>
  )
}

function LabelItem({ label }: { label: Label }): ReactNode {
  return <DeletedLabelIndicator label={label} />
}
